import * as React from 'react';

import { enumRouteSortOption } from '@rematter/frontend-lib-api-client';
import { useTranslations } from '@rematter/modules/transportation/i18n/useTranslations';
import { ComboBox, useComboBoxOptions } from '@rematter/ui-web';

import { useRoutesQuery } from '~/modules/transportation/hooks/queries/useRoutesQuery';

interface RouteSelectProps {
  label: string;
  value: string;
  onChange: (value: React.Key | null, driverId?: string) => void;
  isRequired?: boolean;
  isDisabled?: boolean;
}

const sorting = [enumRouteSortOption.CreatedAtDesc];

export const RouteSelect = ({
  label,
  value,
  onChange,
  isRequired,
  isDisabled,
}: RouteSelectProps) => {
  const t = useTranslations();
  const { data } = useRoutesQuery({
    sorting,
    limit: 100,
  });

  const options = React.useMemo(() => data?.routes ?? [], [data?.routes]);

  const routeOptions = React.useMemo(
    () =>
      options.map((route) => ({
        id: route.id,
        label: `Route #${route.documentNumber}`,
      })),
    [options],
  );

  const { query, onChangeQuery, filteredOptions } = useComboBoxOptions({
    value: value || '',
    options: routeOptions,
    disabled: isDisabled,
  });

  const handleSelectionChange = React.useCallback(
    (values: string[]) => {
      const selectedValue = values[0] || null;
      const selectedRoute = options.find((route) => route.id === selectedValue);
      onChange?.(selectedValue, selectedRoute?.driverId);
    },
    [onChange, options],
  );

  return (
    <ComboBox
      label={label}
      value={value ? [value] : []}
      isRequired={isRequired}
      isDisabled={isDisabled}
      onChange={handleSelectionChange}
      query={query}
      onChangeQuery={onChangeQuery}
      placeholder={t.servicesView.form.placeholders.route}
      className="@RouteSelect"
      options={filteredOptions}
      getSelection={(ids) =>
        ids.map((id) => {
          const route = options.find((r) => r.id === id);
          return {
            label: route ? `Route #${route.documentNumber}` : '',
          };
        })
      }
    />
  );
};
